import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, CircularProgress, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import {
  canonicalPeriodLabels,
  CanonicalPeriod,
  EmissionTier,
  MetricState,
  metricStateLabels,
  PERIODS,
} from '../../common'
import { usePropertyClimateData, type PropertyResult } from '../../../../hooks/climateData'
import { WaterRiskScoreBadge } from '../../common/waterRisk/utils'

type Props = {
  propertyId: number
  emissionTier: EmissionTier
}

type PeriodSnapshot = {
  category: number | null
  description: string | null
  demand: number | null
  demandYear: number | null
  supply: number | null
  supplyYear: number | null
}

const emptySnapshot: PeriodSnapshot = {
  category: null,
  description: null,
  demand: null,
  demandYear: null,
  supply: null,
  supplyYear: null,
}

function parseCategory(result: PropertyResult | undefined, tierKey: string, period: CanonicalPeriod): number | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const node: any = result.data?.data?.[tierKey]?.category?.[period]
  const raw = typeof node?.y !== 'undefined' ? node?.y : node
  const value =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? Number.parseFloat(raw)
      : raw && typeof raw === 'object'
      ? typeof (raw as any).y === 'number'
        ? (raw as any).y
        : typeof (raw as any).y === 'string'
        ? Number.parseFloat((raw as any).y)
        : null
      : null
  return typeof value === 'number' && !Number.isNaN(value) ? value : null
}

function parseDescription(result: PropertyResult | undefined, tierKey: string, period: CanonicalPeriod): string | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  const node: any = result.data?.data?.[tierKey]?.description?.[period]
  const raw = typeof node?.y !== 'undefined' ? node?.y : node
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number') return String(raw)
  return null
}

function parsePoint(
  result: PropertyResult | undefined,
  tierKey: string,
  period: CanonicalPeriod,
): { value: number | null; year: number | null } {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) {
    return { value: null, year: null }
  }
  const node = result.data?.data?.[tierKey]?.[period]
  const rawValue =
    typeof node?.y === 'number'
      ? node.y
      : typeof node?.y === 'string'
      ? Number.parseFloat(node.y)
      : typeof node === 'number'
      ? node
      : typeof node === 'string'
      ? Number.parseFloat(node)
      : null
  const value = typeof rawValue === 'number' && !Number.isNaN(rawValue) ? rawValue : null
  const year = typeof node?.x === 'number' ? node.x : null
  return { value, year }
}

const WaterRiskOverview: React.FC<Props> = ({ propertyId, emissionTier }) => {
  const tierKey = emissionTier as unknown as string

  const {
    data: waterStress,
    isLoading: waterStressLoading,
    error: waterStressError,
  } = usePropertyClimateData('wr:ws_info', { propertyId })
  const {
    data: waterDemand,
    isLoading: waterDemandLoading,
    error: waterDemandError,
  } = usePropertyClimateData('wr:ww', { propertyId })
  const {
    data: blueWater,
    isLoading: blueWaterLoading,
    error: blueWaterError,
  } = usePropertyClimateData('wr:ba', { propertyId })

  const snapshots = useMemo(() => {
    const out: Record<CanonicalPeriod, PeriodSnapshot> = {
      [CanonicalPeriod.SHORT_TERM]: { ...emptySnapshot },
      [CanonicalPeriod.MID_TERM]: { ...emptySnapshot },
      [CanonicalPeriod.LONG_TERM]: { ...emptySnapshot },
    }

    PERIODS.forEach(period => {
      const pointDemand = parsePoint(waterDemand, tierKey, period)
      const pointSupply = parsePoint(blueWater, tierKey, period)
      out[period] = {
        category: parseCategory(waterStress, tierKey, period),
        description: parseDescription(waterStress, tierKey, period),
        demand: pointDemand.value,
        demandYear: pointDemand.year,
        supply: pointSupply.value,
        supplyYear: pointSupply.year,
      }
    })

    return out
  }, [blueWater, tierKey, waterDemand, waterStress])

  const availablePeriods = useMemo(
    () =>
      PERIODS.filter(period => {
        const snapshot = snapshots[period]
        return snapshot.category != null || !!snapshot.description || snapshot.demand != null || snapshot.supply != null
      }),
    [snapshots],
  )

  const defaultPeriod = availablePeriods[0] ?? CanonicalPeriod.SHORT_TERM
  const [selectedPeriod, setSelectedPeriod] = useState<CanonicalPeriod>(defaultPeriod)

  useEffect(() => {
    setSelectedPeriod(defaultPeriod)
  }, [defaultPeriod])

  const effectivePeriod = selectedPeriod ?? defaultPeriod
  const current = snapshots[effectivePeriod]

  const loading = waterStressLoading || waterDemandLoading || blueWaterLoading
  const hasData = availablePeriods.length > 0
  const allErrored = !!(waterStressError && waterDemandError && blueWaterError)

  if (loading && !hasData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Unable to load water risk metrics for this property.</Alert>
  }

  if (!hasData) {
    return <Alert severity="info">No water risk data is available for this property.</Alert>
  }

  console.log(current)
  const balance = current.demand != null && current.supply != null ? current.supply - current.demand : null

  return (
    <Box>
      <Box
        mb={3}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <Box>
          <Typography variant="h6">Overview</Typography>
          <Typography variant="body2" color="text.secondary">
            Summary of water risk for the selected period
          </Typography>
        </Box>

        {availablePeriods.length > 1 && (
          <ToggleButtonGroup
            size="small"
            value={effectivePeriod}
            exclusive
            onChange={(_, period) => {
              if (period) setSelectedPeriod(period)
            }}>
            {availablePeriods.map(period => (
              <ToggleButton key={period} value={period}>
                {canonicalPeriodLabels[period]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" height="100%" gap={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Risk Rating
            </Typography>
            {current.category != null ? (
              <Box>
                <WaterRiskScoreBadge score={current.category} size="regular" />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {waterStress?.state?.value && waterStress.state.value !== MetricState.SUCCEEDED
                  ? `Water stress data is ${metricStateLabels[waterStress.state.value]}.`
                  : 'Water stress data is unavailable for this period.'}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" height="100%" gap={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Water Demand {current.demandYear ? `(${current.demandYear})` : ''}
            </Typography>
            {current.demand != null ? (
              <>
                <Typography variant="h4" component="div">
                  {current.demand.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total annual water demanded by households, farms and industry in local basin, shown as water depth
                  (cm/year).
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {waterDemand?.state?.value && waterDemand.state.value !== MetricState.SUCCEEDED
                  ? `Water demand data is ${metricStateLabels[waterDemand.state.value]}.`
                  : 'Water demand data is unavailable for this period.'}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" height="100%" gap={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Available Water Supply {current.supplyYear ? `(${current.supplyYear})` : ''}
            </Typography>
            {current.supply != null ? (
              <>
                <Typography variant="h4" component="div">
                  {current.supply.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average annual renewable freshwater available in local basin, shown as water depth (cm/year).
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {blueWater?.state?.value && blueWater.state.value !== MetricState.SUCCEEDED
                  ? `Blue water data is ${metricStateLabels[blueWater.state.value]}.`
                  : 'Blue water data is unavailable for this period.'}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {balance != null && (
        <Box mt={3}>
          <Typography variant="subtitle2">Balance</Typography>
          <Typography variant="body2" color="text.secondary">
            {balance >= 0
              ? `Projected supply exceeds demand by ${balance.toFixed(2)} cm/year.`
              : `Projected demand exceeds supply by ${(balance * -1).toFixed(2)} cm/year.`}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default WaterRiskOverview
